// Rendu des 750 variantes : matériau du style, détail de famille, galons de rang, marques des tubes.
// Tout est dessiné dans la zone visible de la caisse (coins, avant, arrière, chenilles) ou dans l'anneau de
// la tourelle : jamais sur l'axe des tubes ni au-delà de la silhouette du tank (hitbox inchangée).
import type { Graphics } from 'pixi.js';
import type { ClassNode } from '../../../../shared/catalog/classes';
import { STYLE_BY_CODE, rankPips, type StyleId } from '../../../../shared/catalog/variants';
import { COSMETICS } from '../../../../shared/shop';
import { ART_R, PALETTES, drawAutoTurrets, drawHull, drawHullFamily, drawTurret, type Palette } from './art';

const R = ART_R;
const WHITE = 0xffffff;
const GLOW = 0xa58bff;
const AMBER = 0xffc232;
const HAZARD = 0x1b1b1d;

/** Générateur déterministe par classe : une variante se dessine pareil chez tous les joueurs. */
function seeded(seed: number): () => number {
  let s = (seed * 2654435761 + 0x9e3779b9) >>> 0;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 4294967296;
  };
}

/** Arc seul (Pixi relie sinon l'arc au point courant du tracé). */
function arcAt(g: Graphics, cx: number, cy: number, r: number, a0: number, a1: number): Graphics {
  return g.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r).arc(cx, cy, r, a0, a1);
}

function star(g: Graphics, x: number, y: number, r: number, color: number, alpha: number): void {
  for (let i = 0; i < 3; i++) {
    const a = (i * Math.PI) / 3;
    g.moveTo(x - Math.cos(a) * r, y - Math.sin(a) * r)
      .lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r)
      .stroke({ width: 1.2, color, alpha });
  }
}

/** Matériau du style sur la caisse et les chenilles (repère de la caisse, nez vers +x). */
export function drawStyleHull(g: Graphics, cls: ClassNode, style: StyleId, p: Palette): void {
  const rnd = seeded(cls.index * 7 + 3);
  switch (style) {
    case 'atelier': {
      // Acier brossé : reflet sur l'arête, grille d'échappement, boulons des chenilles.
      g.moveTo(-0.58 * R, -0.47 * R).lineTo(0.58 * R, -0.47 * R).stroke({ width: 1.4, color: WHITE, alpha: 0.35 });
      for (let i = -1; i <= 1; i++) g.moveTo(-0.6 * R, i * 0.09 * R - 0.03 * R).lineTo(-0.6 * R, i * 0.09 * R + 0.03 * R).stroke({ width: 1.5, color: p.outline, alpha: 0.8 });
      for (const side of [-1, 1]) for (const x of [-0.6, 0.6]) g.circle(x * R, side * 0.66 * R, 0.035 * R).fill(p.plate);
      break;
    }
    case 'desert': {
      // Taches de camouflage aux coins, sacs de sable à l'arrière, poussière sur les chenilles.
      const spots = [
        [-0.55, -0.4],
        [0.52, -0.38],
        [-0.5, 0.42],
        [0.56, 0.4],
        [0.62, 0],
      ];
      for (const [x, y] of spots) {
        const rx = (0.06 + rnd() * 0.05) * R;
        g.ellipse((x + (rnd() - 0.5) * 0.06) * R, (y + (rnd() - 0.5) * 0.06) * R, rx, rx * (0.6 + rnd() * 0.3)).fill({ color: rnd() < 0.5 ? p.band : 0x9c7f52, alpha: 0.85 });
      }
      for (const side of [-1, 1]) g.roundRect(-0.74 * R, side * 0.26 * R - 0.07 * R, 0.14 * R, 0.14 * R, 0.05 * R).fill(0xcdb78c).stroke({ width: 1.2, color: p.outline });
      for (let i = 0; i < 8; i++) {
        const side = i % 2 ? 1 : -1;
        g.circle((rnd() - 0.5) * 1.2 * R, side * (0.66 + (rnd() - 0.5) * 0.2) * R, 0.025 * R).fill({ color: 0xe2cfa1, alpha: 0.8 });
      }
      break;
    }
    case 'boreal': {
      // Givre aux coins, liseré de neige à l'avant, chenilles cloutées.
      for (const [x, y] of [
        [-0.55, -0.42],
        [0.55, -0.42],
        [-0.55, 0.42],
        [0.55, 0.42],
      ])
        star(g, x * R, y * R, 0.07 * R, WHITE, 0.75);
      g.roundRect(0.56 * R, -0.46 * R, 0.07 * R, 0.92 * R, 0.03 * R).fill({ color: WHITE, alpha: 0.55 });
      for (const side of [-1, 1]) for (let i = -3; i <= 3; i++) g.circle(i * 0.18 * R + 0.09 * R, side * 0.66 * R, 0.028 * R).fill({ color: 0xe8f4ff, alpha: 0.9 });
      break;
    }
    case 'obsidienne': {
      // Facettes aux coins et jointures lumineuses le long des arêtes et des chenilles.
      for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ])
        g.poly([sx * 0.62 * R, sy * 0.5 * R, sx * 0.42 * R, sy * 0.5 * R, sx * 0.62 * R, sy * 0.3 * R]).fill({ color: p.plate, alpha: 0.95 }).stroke({ width: 1, color: GLOW, alpha: 0.7 });
      g.roundRect(-0.6 * R, -0.48 * R, 1.2 * R, 0.96 * R, 0.15 * R).stroke({ width: 1.2, color: GLOW, alpha: 0.55 });
      for (const side of [-1, 1]) g.moveTo(-0.62 * R, side * 0.66 * R).lineTo(0.62 * R, side * 0.66 * R).stroke({ width: 1.2, color: GLOW, alpha: 0.6 });
      break;
    }
    case 'signal': {
      // Chevrons de danger sur les gardes avant des chenilles, liseré arrière.
      for (const side of [-1, 1]) {
        const y = side * 0.66 * R;
        g.roundRect(0.36 * R, y - 0.2 * R, 0.34 * R, 0.4 * R, 0.08 * R).fill(0xf08a3a).stroke({ width: 1.5, color: p.outline });
        for (let k = 0; k < 3; k++) {
          const x = 0.4 * R + k * 0.1 * R;
          g.poly([x, y - 0.18 * R, x + 0.05 * R, y - 0.18 * R, x + 0.11 * R, y + 0.18 * R, x + 0.06 * R, y + 0.18 * R]).fill(HAZARD);
        }
      }
      g.moveTo(-0.62 * R, -0.45 * R).lineTo(-0.62 * R, 0.45 * R).stroke({ width: 2, color: AMBER, alpha: 0.9 });
      break;
    }
  }
}

/** Détail de famille sur la caisse (coins arrière) et galons de rang (arrière, au centre). */
export function drawFamilyDetail(g: Graphics, cls: ClassNode, p: Palette): void {
  const back = -0.57 * R;
  const cornerY = 0.36 * R;
  switch (cls.family) {
    case 'Assaut':
      g.roundRect(back - 0.07 * R, cornerY - 0.08 * R, 0.17 * R, 0.16 * R, 0.03 * R).fill(p.band).stroke({ width: 1.2, color: p.outline });
      g.moveTo(back - 0.04 * R, cornerY).lineTo(back + 0.07 * R, cornerY).stroke({ width: 1, color: p.outline });
      break;
    case 'Précision':
      g.circle(0.55 * R, -0.38 * R, 0.06 * R).fill(p.plate).stroke({ width: 1.2, color: p.outline });
      g.circle(0.55 * R, -0.38 * R, 0.025 * R).fill(0x7fd4ff);
      break;
    case 'Artillerie':
      for (const sy of [-1, 1]) for (const sx of [-1, 1]) g.moveTo(sx * 0.6 * R, sy * 0.48 * R).lineTo(sx * 0.74 * R, sy * 0.56 * R).stroke({ width: 2.2, color: p.band });
      break;
    case 'Missiles':
      for (const sy of [-1, 1]) {
        g.roundRect(back - 0.12 * R, sy * 0.66 * R - 0.09 * R, 0.2 * R, 0.18 * R, 0.04 * R).fill(p.band).stroke({ width: 1.2, color: p.outline });
        g.circle(back - 0.05 * R, sy * 0.66 * R - 0.04 * R, 0.025 * R).fill(p.outline);
        g.circle(back - 0.05 * R, sy * 0.66 * R + 0.04 * R, 0.025 * R).fill(p.outline);
      }
      break;
    case 'Lasers':
      for (const sy of [-1, 1]) g.circle(back, sy * cornerY, 0.06 * R).stroke({ width: 2, color: 0x7fe7ff }).circle(back, sy * cornerY, 0.02 * R).fill(0x7fe7ff);
      break;
    case 'Drones':
      g.moveTo(back, cornerY).lineTo(back - 0.16 * R, cornerY + 0.12 * R).stroke({ width: 1.5, color: p.outline });
      g.circle(back - 0.16 * R, cornerY + 0.12 * R, 0.04 * R).fill(0xff6b5a);
      break;
    case 'Mines':
      for (const sy of [-1, 1]) g.poly([back - 0.06 * R, sy * (cornerY - 0.07 * R), back + 0.08 * R, sy * cornerY, back - 0.06 * R, sy * (cornerY + 0.07 * R)]).fill(0xe0b030).stroke({ width: 1, color: p.outline });
      break;
    case 'Pièges':
      for (const sy of [-1, 1]) arcAt(g, back - 0.04 * R, sy * cornerY, 0.07 * R, sy > 0 ? 0 : Math.PI, sy > 0 ? Math.PI : Math.PI * 2).stroke({ width: 2, color: p.band });
      break;
    case 'Collision':
      for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ])
        g.moveTo(sx * 0.64 * R, sy * 0.3 * R).lineTo(sx * 0.64 * R, sy * 0.52 * R).lineTo(sx * 0.44 * R, sy * 0.52 * R).stroke({ width: 2.5, color: p.band });
      break;
    case 'Dispersion':
      for (const sy of [-1, 1]) for (let i = 0; i < 3; i++) g.moveTo(-0.2 * R + i * 0.14 * R, sy * 0.5 * R).lineTo(-0.14 * R + i * 0.14 * R, sy * 0.44 * R).stroke({ width: 1.5, color: p.outline });
      break;
    case 'Rebond':
      for (const sy of [-1, 1]) g.poly([0.5 * R, sy * 0.5 * R, 0.66 * R, sy * 0.38 * R, 0.62 * R, sy * 0.52 * R]).fill(p.plate).stroke({ width: 1.2, color: p.outline });
      break;
    case 'Tourelles':
      g.circle(back - 0.04 * R, 0, 0.07 * R).fill(p.plate).stroke({ width: 1.2, color: p.outline });
      g.circle(back - 0.04 * R, 0, 0.03 * R).fill(0xff5a3c);
      return; // les galons passent sur les côtés pour cette famille
    case 'Énergie':
      for (const sy of [-1, 1]) g.roundRect(back - 0.04 * R, sy * cornerY - 0.05 * R, 0.13 * R, 0.1 * R, 0.05 * R).fill(0x6ff0c8).stroke({ width: 1.2, color: p.outline });
      break;
    case 'Contrôle':
      arcAt(g, back, cornerY, 0.08 * R, Math.PI * 0.6, Math.PI * 1.6).stroke({ width: 2, color: p.plate });
      g.circle(back, cornerY, 0.02 * R).fill(p.outline);
      break;
    case 'Hybrides':
      g.roundRect(back - 0.07 * R, cornerY - 0.08 * R, 0.17 * R, 0.16 * R, 0.03 * R).fill(p.band).stroke({ width: 1.2, color: p.outline });
      g.moveTo(back, -cornerY).lineTo(back - 0.16 * R, -cornerY - 0.12 * R).stroke({ width: 1.5, color: p.outline });
      g.circle(back - 0.16 * R, -cornerY - 0.12 * R, 0.04 * R).fill(0x7fe7ff);
      break;
  }
}

/** Galons de rang : chevrons à l'arrière de la caisse (1 à 4 selon le niveau de la classe). */
export function drawRankPips(g: Graphics, cls: ClassNode, p: Palette): void {
  const n = rankPips(cls);
  const x = -0.6 * R;
  for (let i = 0; i < n; i++) {
    const y = (i - (n - 1) / 2) * 0.1 * R;
    g.poly([x + 0.04 * R, y - 0.04 * R, x - 0.02 * R, y, x + 0.04 * R, y + 0.04 * R]).stroke({ width: 1.6, color: 0xf2d16b });
  }
  void p;
}

/** Anneau de la tourelle (entre le cœur coloré et le bord) selon le style. */
export function drawStyleTurret(g: Graphics, cls: ClassNode, style: StyleId, p: Palette): void {
  const rnd = seeded(cls.index * 11 + 5);
  switch (style) {
    case 'atelier':
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
        g.circle(Math.cos(a) * 0.41 * R, Math.sin(a) * 0.41 * R, 0.035 * R).fill(p.plate);
      }
      break;
    case 'desert':
      for (let i = 0; i < 3; i++) {
        const a = rnd() * Math.PI * 2;
        arcAt(g, 0, 0, 0.41 * R, a, a + 0.6 + rnd() * 0.5).stroke({ width: 0.12 * R, color: 0x9c7f52, alpha: 0.9 });
      }
      break;
    case 'boreal':
      g.circle(0, 0, 0.44 * R).stroke({ width: 1.4, color: WHITE, alpha: 0.6 });
      star(g, -0.36 * R, 0.2 * R, 0.05 * R, WHITE, 0.8);
      break;
    case 'obsidienne':
      g.regularPoly(0, 0, 0.45 * R, 6, Math.PI / 6).stroke({ width: 1.4, color: GLOW, alpha: 0.85 });
      break;
    case 'signal':
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        arcAt(g, 0, 0, 0.41 * R, a - 0.18, a + 0.18).stroke({ width: 0.1 * R, color: HAZARD });
      }
      // Gyrophare à l'arrière de la tourelle (opposé aux tubes avant).
      g.circle(-0.4 * R, 0, 0.09 * R).fill({ color: AMBER, alpha: 0.3 }).circle(-0.4 * R, 0, 0.055 * R).fill(AMBER).stroke({ width: 1, color: p.outline });
      break;
  }
}

/** Marques fines près de l'embouchure des tubes : jamais sur l'axe de tir ni au-delà du tube. */
export function drawBarrelMarks(g: Graphics, cls: ClassNode, style: StyleId): void {
  if (style === 'atelier') return;
  for (const b of cls.weapon.barrels) {
    if (b.auto) continue;
    const look = b.look ?? 'tube';
    if (look !== 'tube' && look !== 'launcher') continue;
    const a = (b.angle * Math.PI) / 180;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const w = b.width * R;
    const L = b.length * R;
    const off = b.offset * R;
    const line = (x: number, color: number, alpha: number, width = 1.4) => {
      const p1 = [x * cos - (off - w * 0.42) * sin, x * sin + (off - w * 0.42) * cos];
      const p2 = [x * cos - (off + w * 0.42) * sin, x * sin + (off + w * 0.42) * cos];
      g.moveTo(p1[0], p1[1]).lineTo(p2[0], p2[1]).stroke({ width, color, alpha });
    };
    if (style === 'desert') {
      line(L - 0.13 * R, 0xe8d6a8, 0.95);
      line(L - 0.18 * R, 0xe8d6a8, 0.95);
    } else if (style === 'boreal') line(L - 0.03 * R, WHITE, 0.85, 2);
    else if (style === 'obsidienne') line(L - 0.03 * R, GLOW, 0.95, 2);
    else if (style === 'signal') line(L - 0.11 * R, HAZARD, 1, 2.4);
  }
}

/** Petite marque de style sur les drones (la couleur du camp reste celle du tireur, pour la lisibilité). */
export function droneMark(g: Graphics, style: StyleId): void {
  switch (style) {
    case 'desert':
      g.circle(0, 0, 2.6).fill(0xe2cfa1);
      break;
    case 'boreal':
      g.poly([3, 0, 0, 3, -3, 0, 0, -3]).fill(WHITE);
      break;
    case 'obsidienne':
      g.moveTo(-5, 0).lineTo(7, 0).stroke({ width: 1.6, color: GLOW });
      break;
    case 'signal':
      g.circle(-1, 0, 2.6).fill(AMBER);
      break;
    default:
      break;
  }
}

/** Palette et style d'un code de skin : 0–5 styles de base (0 = Atelier par défaut), 10+i cosmétique i. */
export function lookForCode(code: number): { palette: Palette; style: StyleId | null } {
  if (code >= 10) return { palette: COSMETICS[code - 10]?.palette ?? PALETTES.atelier, style: null };
  const style = STYLE_BY_CODE[code] ?? 'atelier';
  return { palette: PALETTES[style], style };
}

/**
 * Dessine un tank complet pour une variante : caisse (matériau, famille, galons), tourelle et tubes,
 * tourelles automatiques. Les cosmétiques de la boutique gardent les détails de famille et de rang.
 */
export function drawTankParts(parts: { hull: Graphics; turret: Graphics; auto: Graphics }, cls: ClassNode, code: number, accent: number, decor = 0): boolean {
  const { palette, style } = lookForCode(code);
  drawHull(parts.hull, palette, decor);
  drawHullFamily(parts.hull, cls, palette);
  if (style) drawStyleHull(parts.hull, cls, style, palette);
  drawFamilyDetail(parts.hull, cls, palette);
  drawRankPips(parts.hull, cls, palette);
  drawTurret(parts.turret, cls, palette, accent);
  if (style) {
    drawBarrelMarks(parts.turret, cls, style);
    drawStyleTurret(parts.turret, cls, style, palette);
  }
  return drawAutoTurrets(parts.auto, cls, palette, accent);
}
